import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useGlobalContext } from '../../../../utils/Context'
import {
  getDebtSummary,
  getDebtBatches,
} from '../../../../utils/DebtBatchesApi'
import FormLoader from '../../../../components/loader/FormLoader'
import Pagination from '../../../../components/pagination/Pagination'

function DebtBatches() {
  const { notify } = useGlobalContext()
  const [summary, setSummary] = useState<any>(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [batches, setBatches] = useState<any>({ batches: [], pagination: {} })
  const [batchesLoading, setBatchesLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('active')

  // Fetch debt summary
  function fetchSummary() {
    setSummaryLoading(true)
    getDebtSummary()
      .then((result: any) => {
        if (result.status === 200) {
          setSummary(result.data)
        } else {
          notify('error', result.message)
        }
        setSummaryLoading(false)
      })
      .catch(() => {
        setSummaryLoading(false)
      })
  }

  // Fetch debt batches
  function fetchBatches(status: string, page: number, pageSize: number) {
    setBatchesLoading(true)
    getDebtBatches(status, page, pageSize)
      .then((result: any) => {
        if (result.status === 200) {
          setBatches(result.data)
        } else {
          notify('error', result.message)
        }
        setBatchesLoading(false)
      })
      .catch(() => {
        setBatchesLoading(false)
      })
  }

  useEffect(() => {
    fetchSummary()
    fetchBatches(statusFilter, 1, 10)
  }, [])

  // Handle status filter change
  const handleStatusChange = (e: any) => {
    const newStatus = e.target.value
    setStatusFilter(newStatus)
    fetchBatches(newStatus, 1, 10)
  }

  // Get status badge class
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning'
      case 'overdue':
        return 'error'
      case 'cleared':
        return 'success'
      default:
        return ''
    }
  }

  return (
    <div className='home'>
      <div className='home_tag flex justify-between items-center'>
        <h1>Debt Batches</h1>
        <div className='flex items-center select-wrapper'>
          <div className='flex items-center'>
            <p>Status:</p>
            <select value={statusFilter} onChange={handleStatusChange}>
              <option value='active'>Active</option>
              <option value='pending'>Pending</option>
              <option value='overdue'>Overdue</option>
              <option value='cleared'>Cleared</option>
              <option value='all'>All</option>
            </select>
          </div>
        </div>
      </div>

      {/* Debt Summary Cards */}
      <div
        className='home-head flex justify-between flex-wrap'
        style={{ marginTop: 0 }}
      >
        <div className='left flex justify-between flex-wrap'>
          <div className='card'>
            <p>Total Outstanding</p>
            <h2>
              ₦
              {summaryLoading
                ? '....'
                : summary?.totalOutstanding?.toLocaleString() || '0'}
            </h2>
          </div>
          <div className='card'>
            <p>Overdue Amount</p>
            <h2 style={{ color: summary?.overdueAmount > 0 ? '#e74c3c' : '' }}>
              ₦
              {summaryLoading
                ? '....'
                : summary?.overdueAmount?.toLocaleString() || '0'}
            </h2>
          </div>
          <div className='card'>
            <p>Pending Amount</p>
            <h2>
              ₦
              {summaryLoading
                ? '....'
                : summary?.pendingAmount?.toLocaleString() || '0'}
            </h2>
          </div>
          <div className='card'>
            <p>Booking Status</p>
            <h2 style={{ fontSize: '1.2rem' }}>
              {summaryLoading ? (
                '....'
              ) : summary?.isBlocked ? (
                <span style={{ color: '#e74c3c' }}>Blocked</span>
              ) : (
                <span style={{ color: '#27ae60' }}>Active</span>
              )}
            </h2>
          </div>
        </div>
      </div>

      {/* Grace Period Alert */}
      {summary?.hasGrace && (
        <div
          style={{
            padding: '1rem',
            margin: '1rem 0',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <Icon
            icon='mdi:information'
            style={{ marginRight: '0.5rem', color: '#856404' }}
          />
          <strong>Grace Period Active:</strong> Extended until{' '}
          {new Date(summary.graceUntil).toLocaleString()}
        </div>
      )}

      {/* Blocking Alert */}
      {summary?.isBlocked && !summary?.hasGrace && (
        <div
          style={{
            padding: '1rem',
            margin: '1rem 0',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
          }}
        >
          <Icon
            icon='mdi:alert-circle'
            style={{ marginRight: '0.5rem', color: '#721c24' }}
          />
          <strong>Booking Suspended:</strong> You have ₦
          {summary?.overdueAmount?.toLocaleString()} in overdue debt. Please
          clear to continue booking.
        </div>
      )}

      {/* Debt Batches Table */}
      <div className='home_tables'>
        <div className='left'>
          {batchesLoading ? (
            <FormLoader position='center' />
          ) : (
            <>
              {batches.batches.length > 0 ? (
                <>
                  <div className='mobile-table'>
                    {batches.batches.map((item: any, i: number) => (
                      <div className='card' key={item._id}>
                        <div className='flex justify-between row'>
                          <div className='count flex items-center justify-center'>
                            {i + 1}
                          </div>
                          <p>{item.formattedDeadline}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Window Start</h4>
                          <p>{new Date(item.windowStart).toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Remaining Amount</h4>
                          <p>₦{item.formattedAmount}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Total Amount</h4>
                          <p>₦{item.totalAmount.toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Amount Paid</h4>
                          <p>₦{item.amountPaid.toLocaleString()}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Bookings</h4>
                          <p>{item.bookingCount}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Status</h4>
                          <span
                            className={`chip ${getStatusClass(item.status)}`}
                          >
                            {item.status}
                          </span>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Hours Until Deadline</h4>
                          <p
                            style={{
                              color:
                                item.hoursUntilDeadline < 0 ? '#e74c3c' : '',
                            }}
                          >
                            {item.hoursUntilDeadline > 0
                              ? `${item.hoursUntilDeadline.toFixed(1)} hours`
                              : `${Math.abs(item.hoursUntilDeadline).toFixed(1)} hours overdue`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Window Start</th>
                        <th>Deadline</th>
                        <th>Remaining</th>
                        <th>Total</th>
                        <th>Paid</th>
                        <th>Bookings</th>
                        <th>Status</th>
                        <th>Hours Left</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.batches.map((item: any, i: number) => (
                        <tr key={item._id}>
                          <td>{i + 1}</td>
                          <td className='count'>
                            {new Date(item.windowStart).toLocaleString()}
                          </td>
                          <td className='count'>{item.formattedDeadline}</td>
                          <td className='count'>NGN {item.formattedAmount}</td>
                          <td>NGN {item.totalAmount.toLocaleString()}</td>
                          <td>NGN {item.amountPaid.toLocaleString()}</td>
                          <td>{item.bookingCount}</td>
                          <td>
                            <span
                              className={`chip ${getStatusClass(item.status)}`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td
                            style={{
                              color:
                                item.hoursUntilDeadline < 0 ? '#e74c3c' : '',
                            }}
                          >
                            {item.hoursUntilDeadline > 0
                              ? `${item.hoursUntilDeadline.toFixed(1)}h`
                              : `${Math.abs(item.hoursUntilDeadline).toFixed(1)}h overdue`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={batches.pagination.page}
                    totalCount={batches.pagination.totalPages}
                    pageSize={batches.pagination.limit}
                    onPageChange={(page: number, pageSize: number) => {
                      fetchBatches(statusFilter, page, pageSize)
                    }}
                  />
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='la:clipboard-list' />
                    <h1>No Debt Batches To Show</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default DebtBatches
